/**
 * ColorSwatch — small round paint sample.
 * Renders a fixed-size circle filled with a literal CSS color. The swatch is
 * decorative by default; pass `label` for screen readers when used standalone.
 *
 * `vehicleColorToHex(name)` is a fuzzy lookup that mirrors the design bundle's
 * `colorSwatchClass` helper — it normalises Czech / brand names to one of
 * eleven hex values defined in the artboard.
 *
 * Source: .agents/design/project/artboard-radical-vozidla.jsx:186-280
 */

const SWATCHES: Array<[match: RegExp, hex: string]> = [
  [/area\s*51/i, '#6f9c4d'],
  [/eruption/i, '#04ce8a'],
  [/arctic/i, '#c8d8e9'],
  [/blue\s*my\s*mind|bmm/i, '#1c4480'],
  [/vapor/i, '#5d6e80'],
  [/žlut|zlut|yellow/i, '#f4d300'],
  [/čern|cern|black/i, '#1a1a1a'],
  [/bíl|bil|white/i, '#f8f9fa'],
  [/zelen|green/i, '#2a8a44'],
  [/modr|blue/i, '#1c4480'],
  [/červen|cerven|red/i, '#c8302a'],
  [/oranž|oranz|orange/i, '#e07b2a'],
  [/fialov|purple|violet/i, '#6b3fa0'],
  [/hněd|hned|brown/i, '#7a4a2a'],
  [/růžov|ruzov|pink/i, '#d96aa3'],
  [/stříbr|stribr|solar|silver/i, '#c1c5cb'],
  [/šed|sed|cactus|grey|gray/i, '#8a8c91'],
];

const FALLBACK = '#8a8c91';

export function vehicleColorToHex(name: string | null | undefined): string {
  if (!name) return FALLBACK;
  for (const [pattern, hex] of SWATCHES) {
    if (pattern.test(name)) return hex;
  }
  return FALLBACK;
}

interface ColorSwatchProps {
  /** A CSS color (hex / named / rgb()) — pass `vehicleColorToHex(name)` for vehicle paint. */
  color: string;
  /** Diameter in px. Design default is 8 in lists, 10 in inspectors. */
  size?: number;
  /** Accessible label. Omit to mark decorative. */
  label?: string;
  className?: string;
}

export function ColorSwatch({ color, size = 8, label, className = '' }: ColorSwatchProps) {
  const a11y = label ? { role: 'img' as const, 'aria-label': label } : { 'aria-hidden': true };
  return (
    <span
      {...a11y}
      className={'inline-block flex-shrink-0 rounded-full ' + className}
      style={{
        width: size,
        height: size,
        background: color,
        border: '1px solid rgba(0,0,0,0.15)',
      }}
    />
  );
}
